/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import {
  AIImageAsset,
  Asset,
  AssetType,
  BaseBlockSnapshot,
  BlockPlacer,
  ImageAsset,
  OtherAsset,
  QuoteAsset,
  SongAsset,
  VideoAsset,
} from "@vienna/common";

import { StringUtils } from "../../utilities/StringUtils";
import { StyleConstants } from "../../utilities/StyleConstants";
import { BaseBlock } from "src/classes/block/BaseBlock";
import { EmbedIdentifier, ImageBlock, LinkBlock, TextBlock } from "src/classes/block/block-types";
import { BlockFactory } from "src/classes/block/BlockFactory";
import { Plate } from "src/classes/plate/Plate";
import { FileUtils } from "src/classes/utilities/FileUtils";
import { Toaster } from "src/classes/utilities/Toaster";

export class AssetHandler {
  private plate: Plate;

  constructor(plate: Plate) {
    this.plate = plate;
  }

  public createBlockFromAsset(asset: Asset): void {
    switch (asset.type) {
      case AssetType.Image:
      case AssetType.AIImage:
        this.createImageBlockFromAsset(asset);
        break;
      case AssetType.Video:
        this.createYoutubeBlockFromAsset(asset);
        break;
      case AssetType.Quote:
        this.createTextBlockFromAsset(asset);
        break;
      case AssetType.Song:
        this.createSpotifyBlockFromAsset(asset);
        break;
      case AssetType.Other:
        this.createLinkBlockFromAsset(asset);
        break;
      default:
        throw new Error("Unknown asset type");
    }
  }

  private async createImageBlockFromAsset(asset: ImageAsset | AIImageAsset): Promise<void> {
    const caption = asset.type === AssetType.AIImage ? asset.prompt : asset.query;
    const originalFilename = caption.substring(0, 25);
    try {
      const file = await FileUtils.downloadImageFromURL(asset.url, originalFilename);
      const fileSize = await FileUtils.getPixelSizeOfImageFile(file);
      const baseSnapshot = this.getBaseBlockSnapshot(fileSize?.width, fileSize?.height);
      const snapshot = ImageBlock.newSnapshot(baseSnapshot);
      const block = BlockFactory.create(snapshot, this.plate.socket) as ImageBlock;
      this.plate.createBlock(block);
      block.addFile(file, this.plate.dimensions);
      block.updateCaption(caption);
    } catch (error: any) {
      Toaster.error(error.message || "Failed to create image block from asset...");
    }
  }

  private createYoutubeBlockFromAsset(asset: VideoAsset): void {
    try {
      const url = new URL(asset.url);
      const embedId = EmbedIdentifier.getYoutubeVideoIdFromURL(url);
      if (!embedId) throw new Error();
      const baseSnapshot = this.getBaseBlockSnapshot(400, 240);
      const linkBlockSnapshot = LinkBlock.newSnapshot(baseSnapshot, asset.url);
      const block = BlockFactory.create(linkBlockSnapshot, this.plate.socket);
      this.plate.createBlock(block);
    } catch {
      Toaster.error("Failed to create YouTube embed from asset...");
    }
  }

  private createSpotifyBlockFromAsset(asset: SongAsset): void {
    try {
      const url = new URL(asset.url);
      const embedId = EmbedIdentifier.getSpotifyEmbedIdFromURL(url);
      if (!embedId) throw new Error();
      const baseSnapshot = this.getBaseBlockSnapshot(370, 100);
      const linkBlockSnapshot = LinkBlock.newSnapshot(baseSnapshot, asset.url);
      const block = BlockFactory.create(linkBlockSnapshot, this.plate.socket);
      this.plate.createBlock(block);
    } catch {
      Toaster.error("Failed to create Spotify embed from asset...");
    }
  }

  private createTextBlockFromAsset(asset: QuoteAsset): void {
    const width = 400;
    const height = Math.max(
      StyleConstants.estimateTextBlockHeight(asset.text, width),
      StyleConstants.minBlockHeight
    );
    const baseSnapshot = this.getBaseBlockSnapshot(width, height);
    const snapshot = TextBlock.newSnapshot(baseSnapshot);
    const block = BlockFactory.create(snapshot, this.plate.socket) as TextBlock;
    this.plate.createBlock(block);
    const textContent = StringUtils.convertToSlateContent(asset.text);
    block.immediatelyUpdateTextContent(textContent);
  }

  //@todo
  private createLinkBlockFromAsset(asset: OtherAsset): void {
    const text = `${asset.title}\n\n${asset.url}\n\n##Summary:\n\n${asset.summary}`;
    const width = 400;
    const height = Math.max(StyleConstants.estimateTextBlockHeight(text, width), StyleConstants.minBlockHeight);
    const baseSnapshot = this.getBaseBlockSnapshot(width, height);
    const snapshot = TextBlock.newSnapshot(baseSnapshot);
    const block = BlockFactory.create(snapshot, this.plate.socket) as TextBlock;
    this.plate.createBlock(block);
    const textContent = StringUtils.convertToSlateContent(text);
    block.immediatelyUpdateTextContent(textContent);
  }

  /* -------------------------------------------------- */
  /*            Base Snapshot + Positioning             */
  /* -------------------------------------------------- */

  private getBaseBlockSnapshot(
    width: number = StyleConstants.defaultBlockWidth,
    height: number = StyleConstants.defaultBlockHeight
  ): BaseBlockSnapshot {
    const size = { width, height };
    const coordinates = this.plate.blocks.map((block) => block.coordinates);
    const position = BlockPlacer.findAvailablePosition(this.plate.dimensions.height + 1000, size, coordinates); // send high height, will adjust below if needed. lil jank
    const bottom = position.top + height;
    if (bottom > this.plate.dimensions.height) {
      const newHeight = this.plate.dimensions.height + position.top + 20;
      this.plate.changeHeight(newHeight);
    }
    const args = {
      ...position,
      zIndex: this.plate.highestZIndex.value + 1,
      plateId: this.plate.snapshot.id,
    };
    return BaseBlock.newBaseBlockSnapshot(args);
  }
}
